import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { findMatchingDate, toMonthKey } from '../helpers/dateKeys/dateKeys';
import { getDateRanges } from '../helpers/utilityFunctions/utilityFunctions';
import { DateRange } from '../model/dateRange';

/**
 * Coordinates the "browse a month → load that month's test-dates → pick a day →
 * load that day's data" flow that every active-test / daily-metric header needs.
 *
 * The logic used to be hand-rolled per header across two entangled effects, a
 * mutable ref, and a reducer stale-guard. That handshake was order-dependent:
 * whichever month/day fetch resolved last won, so month arrows and the initial
 * "most recent" selection intermittently showed no data. This hook centralises
 * it and makes it deterministic:
 *
 *  - A single monotonic request token. Every async load captures the token at
 *    issue time and only applies its result if the token is still current, so
 *    out-of-order responses (rapid arrow clicks, month browsing) are dropped
 *    rather than corrected after the fact.
 *  - The month the loaded `dates` describe is tracked in a ref that updates
 *    atomically with the array, so a day is only resolved against dates that
 *    actually belong to its month.
 *  - Selection and browse state are local and reset when `patientId` changes,
 *    so one subject's day can never leak into the next.
 *
 * Where the loaded `dates` and the selected day's data are stored is left to the
 * caller via `fetchMonth` / `onDaySelected`, so a header keeps its own Redux
 * wiring while getting race-free coordination. The returned setters match
 * `MultiDatePicker`'s existing prop types, so that component is untouched.
 */

export interface DayResolution {
  /** The day that became the active selection. */
  day: Date;
  /** The matching entry from the loaded month, or undefined if the day has none. */
  matched: Date | undefined;
  /** Convenience: whether `matched` was found. */
  hasData: boolean;
}

export interface UseMonthlyDatePickerParams {
  /** Identifies the current subject; changing it resets selection + browse state. */
  patientId?: string;
  /**
   * Value that, when it changes, forces a full re-initialisation (reset +
   * reload + re-select). Defaults to `patientId`. Provide a composite key when
   * more than the patient should trigger a fresh start — e.g. the test type, or
   * the "most recent" date once it arrives after a refresh — on a route that
   * does not remount the component.
   */
  resetKey?: string;
  /** Best initial guess for the day to show (persisted date, most-recent, or today). */
  getInitialDate: () => Date;
  /** Fetch the available test-dates for the month spanned by `range`. */
  fetchMonth: (range: DateRange) => Promise<Array<Date | string> | undefined>;
  /**
   * Choose which day to select from a freshly loaded month plus the initial
   * hint. Should return a day within the loaded month.
   */
  resolveInitialDate: (monthDates: Date[], hint: Date) => Date;
  /** Called whenever a concrete day becomes the active selection. */
  onDaySelected: (resolution: DayResolution) => void;
  /** Optional cleanup when the picker unmounts or the patient changes. */
  onReset?: () => void;
  /**
   * When false the hook stays inert — no fetching, selecting, or dispatching.
   * Use it when the picker is hidden (e.g. a specific test is being shown
   * instead of the calendar). Defaults to true.
   */
  enabled?: boolean;
}

export interface UseMonthlyDatePickerResult {
  dates: Date[];
  selectedDate: Date | undefined;
  isLoading: boolean;
  setSelectedDate: Dispatch<SetStateAction<Date | undefined>>;
  setSelectedMonth: Dispatch<SetStateAction<Date | undefined>>;
}

export function useMonthlyDatePicker(
  params: UseMonthlyDatePickerParams
): UseMonthlyDatePickerResult {
  const { patientId, resetKey, enabled = true } = params;
  const effectiveResetKey = resetKey ?? patientId;

  const [dates, setDates] = useState<Date[]>([]);
  const [selectedDate, setSelectedDateState] = useState<Date | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Monotonic token guarding every async load, and the month the current
  // `dates` describe (kept in a ref so it moves atomically with the array).
  const reqRef = useRef<number>(0);
  const loadedMonthKeyRef = useRef<string | undefined>(undefined);
  const selectedDateRef = useRef<Date | undefined>(undefined);
  const datesRef = useRef<Date[]>([]);
  datesRef.current = dates;

  // Latest callbacks, held in a ref so inline callbacks don't retrigger the
  // patient-scoped init effect on every render.
  const cbRef = useRef(params);
  cbRef.current = params;

  const setSelected = useCallback((day: Date | undefined) => {
    selectedDateRef.current = day;
    setSelectedDateState(day);
  }, []);

  const loadMonth = useCallback(
    async (month: Date, token: number): Promise<Date[] | null> => {
      setIsLoading(true);
      try {
        const range: DateRange = getDateRanges(month, true);
        const raw = await cbRef.current.fetchMonth(range);
        if (token !== reqRef.current) return null; // superseded
        const normalised = (raw ?? []).map((d) => new Date(d));
        setDates(normalised);
        loadedMonthKeyRef.current = toMonthKey(month);
        return normalised;
      } finally {
        if (token === reqRef.current) setIsLoading(false);
      }
    },
    []
  );

  // Pick a concrete day: ensure its month's dates are loaded, then report it.
  const selectDay = useCallback(
    async (day: Date) => {
      const token = ++reqRef.current;
      setSelected(day);

      let monthDates = datesRef.current;
      if (toMonthKey(day) !== loadedMonthKeyRef.current) {
        const loaded = await loadMonth(day, token);
        if (loaded === null) return; // superseded while awaiting
        monthDates = loaded;
      }
      if (token !== reqRef.current) return; // superseded

      const matched = findMatchingDate(monthDates, day);
      cbRef.current.onDaySelected({
        day,
        matched,
        hasData: matched !== undefined,
      });
    },
    [loadMonth, setSelected]
  );

  // Browse to a month without changing the selection (calendar month arrows).
  const browseMonth = useCallback(
    (month: Date) => {
      const token = ++reqRef.current;
      void loadMonth(month, token);
    },
    [loadMonth]
  );

  const setSelectedDate = useCallback<Dispatch<SetStateAction<Date | undefined>>>(
    (value) => {
      const next =
        typeof value === 'function'
          ? (value as (prev: Date | undefined) => Date | undefined)(
              selectedDateRef.current
            )
          : value;
      if (next) void selectDay(next);
    },
    [selectDay]
  );

  const setSelectedMonth = useCallback<Dispatch<SetStateAction<Date | undefined>>>(
    (value) => {
      const next =
        typeof value === 'function'
          ? (value as (prev: Date | undefined) => Date | undefined)(undefined)
          : value;
      if (next) browseMonth(next);
    },
    [browseMonth]
  );

  // Initialise on mount and whenever the subject changes. Everything from a
  // previous subject is discarded first so nothing leaks across patients.
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const token = ++reqRef.current;
    loadedMonthKeyRef.current = undefined;
    setDates([]);
    setSelected(undefined);

    const hint = cbRef.current.getInitialDate();
    void (async () => {
      const loaded = await loadMonth(hint, token);
      if (!active || loaded === null || token !== reqRef.current) return;
      const day = cbRef.current.resolveInitialDate(loaded, hint);
      if (token !== reqRef.current) return;
      setSelected(day);
      const matched = findMatchingDate(loaded, day);
      cbRef.current.onDaySelected({
        day,
        matched,
        hasData: matched !== undefined,
      });
    })();

    return () => {
      active = false;
      cbRef.current.onReset?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveResetKey, enabled, loadMonth, setSelected]);

  return {
    dates,
    selectedDate,
    isLoading,
    setSelectedDate,
    setSelectedMonth,
  };
}

export default useMonthlyDatePicker;
